import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  BarChart2,
  Map,
  Radar,
  Newspaper,
  TrendingUp,
  Globe,
  Search,
  FileText,
  LogOut,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import Overview from '../portal/Overview'
import DataCharts from '../portal/DataCharts'
import NewsArchive from '../portal/NewsArchive'
import PolicyDocuments from '../portal/PolicyDocuments'
import VesselMap from '../portal/VesselMap'
import ActivityMap from '../portal/ActivityMap'
import TradeAnalysis from '../portal/TradeAnalysis'
import RegionalAnalysis from '../portal/RegionalAnalysis'
import Investigation from '../portal/Investigation'

const BURGUNDY = '#8B1A1A'
const GOLD = '#B8860B'

const NAV_ITEMS = [
  { id: 'overview',    label: 'Overview',         Icon: LayoutDashboard },
  { id: 'data',        label: 'Data & Charts',     Icon: BarChart2 },
  { id: 'map',         label: 'Vessel Map',        Icon: Map },
  { id: 'activity',   label: 'Activity Map',      Icon: Radar },
  { id: 'news',        label: 'News Archive',      Icon: Newspaper },
  { id: 'trade',       label: 'Trade Analysis',    Icon: TrendingUp },
  { id: 'regional',    label: 'Regional Analysis', Icon: Globe },
  { id: 'investigation', label: 'Investigation',   Icon: Search },
  { id: 'policy',      label: 'Policy Documents',  Icon: FileText },
]

const SECTION_TITLES = {
  overview:      'Overview',
  data:          'Data & Charts',
  map:           'Vessel Map',
  activity:      'Activity Map — Vessels + Aircraft + SST',
  news:          'News Archive',
  trade:         'Trade Analysis',
  regional:      'Regional Analysis',
  investigation: 'Investigation',
  policy:        'Policy Documents',
}

const S = {
  shell: {
    minHeight: '100vh',
    background: '#0a0a0a',
    display: 'flex',
    fontFamily: "'DM Sans', sans-serif",
  },
  sidebar: {
    width: '220px',
    minWidth: '220px',
    background: '#111111',
    borderRight: '1px solid #1a1a1a',
    display: 'flex',
    flexDirection: 'column',
    padding: '1.5rem 0',
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
  },
  sidebarTitle: {
    color: GOLD,
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    padding: '0 1.2rem',
    marginBottom: '1.2rem',
    fontWeight: 700,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    padding: '0.55rem 1.2rem',
    fontSize: '0.84rem',
    color: '#bbb',
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    userSelect: 'none',
    transition: 'color 0.12s',
    background: 'none',
    width: '100%',
    textAlign: 'left',
  },
  navItemActive: {
    color: '#e0e0e0',
    borderLeft: `3px solid ${BURGUNDY}`,
    background: 'rgba(139,26,26,0.08)',
  },
  sidebarBottom: {
    marginTop: 'auto',
    padding: '1rem 1.2rem',
    borderTop: '1px solid #1a1a1a',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.55rem',
    background: 'rgba(139,26,26,0.12)',
    border: '1px solid #5a1a1a',
    borderRadius: '4px',
    color: '#d08080',
    fontSize: '0.82rem',
    fontWeight: 500,
    padding: '0.55rem 0.8rem',
    cursor: 'pointer',
    width: '100%',
    transition: 'color 0.12s, border-color 0.12s, background 0.12s',
  },
  mainWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.8rem 2rem',
    borderBottom: '1px solid #1a1a1a',
    background: '#0d0d0d',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  topBarTitle: {
    fontSize: '0.9rem',
    color: '#ccc',
    fontWeight: 500,
    letterSpacing: '0.02em',
  },
  confidentialBadge: {
    fontSize: '0.62rem',
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    color: '#8B1A1A',
    border: '1px solid #3a0a0a',
    borderRadius: '3px',
    padding: '0.2rem 0.55rem',
    fontWeight: 700,
    background: 'rgba(139,26,26,0.06)',
  },
  main: {
    flex: 1,
    padding: '2rem',
    overflowY: 'auto',
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    color: '#bbb',
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
}

function PlaceholderSection({ name }) {
  return <div style={S.placeholder}>{name} — coming soon</div>
}

function renderSection(section) {
  switch (section) {
    case 'overview':      return <Overview />
    case 'data':          return <DataCharts />
    case 'map':           return <VesselMap />
    case 'activity':      return <ActivityMap />
    case 'news':          return <NewsArchive />
    case 'trade':         return <TradeAnalysis />
    case 'regional':      return <RegionalAnalysis />
    case 'investigation': return <Investigation />
    case 'policy':        return <PolicyDocuments />
    default:              return <PlaceholderSection name={section} />
  }
}

export default function Portal() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [active, setActive] = useState('overview')

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div style={S.shell}>
      {/* Sidebar */}
      <aside style={S.sidebar}>
        <div style={S.sidebarTitle}>Menhaden Policy</div>

        {NAV_ITEMS.map(({ id, label, Icon }) => {
          const isActive = active === id
          return (
            <button
              key={id}
              onClick={() => setActive(id)}
              style={{
                ...S.navItem,
                ...(isActive ? S.navItemActive : {}),
              }}
            >
              <Icon size={14} strokeWidth={isActive ? 2 : 1.5} />
              {label}
            </button>
          )
        })}

        <div style={S.sidebarBottom}>
          {user?.email && (
            <div style={{
              color: '#d0d0d0',
              fontSize: '0.72rem',
              marginBottom: '0.6rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user.email}
            </div>
          )}
          <button style={S.logoutBtn} onClick={handleLogout}>
            <LogOut size={13} strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div style={S.mainWrap}>
        <div style={S.topBar}>
          <div style={S.topBarTitle}>{SECTION_TITLES[active] || active}</div>
          <div style={S.confidentialBadge}>Confidential — Authorized Access Only</div>
        </div>
        <main style={S.main}>
          {renderSection(active)}
        </main>
      </div>
    </div>
  )
}
